import AddIcon from '@mui/icons-material/Add';
import DoneAllSharpIcon from '@mui/icons-material/DoneAllSharp';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import { Grid, Box, FormControlLabel, RadioGroup, ListItemButton } from '@mui/material';
import Radio from '@mui/material/Radio';
import { styled } from '@mui/material/styles';
import { GridColDef, GridRowId, GridSortModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getClientList,
  assignParent,
  getParentList,
  ClientDTO,
  ColumnSettings,
  getClientStatusList,
  ClientRequestDTO,
  getAllClientTags,
  getChildClientsByParentId,
  LogAction,
  LogContextType,
  saveActivityLog,
  IUserDTO,
  getClientById,
} from 'wisestep-api';
import {
  WsTypography,
  WsButton,
  WsTypoVariants,
  EmptyTeamsAndUsersIcon,
  WsIconMoreVer,
  WsColors,
  WsTypoColors,
  Button,
  WsDrawer,
  getDateUsingFormat,
  WsSelectObj,
  WsListingLoader,
  WsIconText,
  WsDataGrid,
  FieldOption,
} from 'wisestep-base';

import { DrawerFooter } from '../../../components/filters/components/AdvanceFilterFooterBar';
import NoDataFoundCard from '../../../components/noDataFoundCard';
import { IBreadCrumbsList } from '../../../components/WsBreadCrumbs';
import { useUserContext, useWorkspaceContext } from '../../../context';
import { activityLogDTOHelper } from '../../../features/activityLog/ActivityHelper';
import ClientStatusUpdateForm from '../../../features/clientDetails/components/clientStatus/ClientStatusUpdateForm';
import { useClientStore } from '../../../features/clientDetails/store/useClientStore';
import CommonHeader from '../../../features/leads/components/CommonHeader';
import { StyledEventNoteIcon } from '../../../features/leads/helper/leadHelper';
import { useClientAuthz, ClientAction } from '../../../hooks/useClientAuthz';
import { useDialog } from '../../../hooks/useDialog';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import ClientUserList from '../../clientDetails/components/clientSharing/ClientUserList';
import {
  clientsAmplitudeEvents,
  jobIndustry,
} from '../../clientDetails/helper/clientDetailsHelper';
import openNotesFormDialog from '../../clientDetails/helper/openNotesFormDialog';
import WsLink from '../../teams/components/WsLink';
import {
  IFilters,
  clientTypes,
  defaultColumnSettings,
  defaultFilters,
  fetchColumnSettings,
  getClientLocation,
  getEncodedBreadCrumbs,
  initialLocation,
  mapToClientAdvanceFilter,
  saveColumnSettingsToDB,
} from '../helper/ClientListingHelper';

import ClientAdvanceFilters from './ClientAdvanceFilters';
import ClientFilters from './ClientFilters';
import ClientOwnerPopUp from './ClientOwnerPopUp';
import CreateClientDrawer from './CreateClientDrawer';
import { CreateClientForm } from './CreateClientForm';
import PopoverOnClick from './PopoverOnClick';

interface IClientList {
  source?: string;
  isChildClient?: boolean;
  requireHeaderBanner?: boolean;
  isChild?: boolean;
  parentName?: string;
  parentClientId?: string;
}

export const BottomBlueBar = styled(Box)(({ theme }) => ({
  zIndex: '1299',
  position: 'fixed',
  bottom: '1.5rem',
  textAlign: 'center',
  left: 'calc(50% + 2.25rem)',
  transform: 'translateX(-50%)',
  borderRadius: '0.5rem',
  background: WsColors.TheBlues900,
  padding: '1rem',
  width: 'calc(100% - 6.5rem)',
  maxWidth: 'calc(' + theme.breakpoints.values.xl + 'px - 2rem)',
}));

const statusOptionsData: WsSelectObj[] = [];

const optionsData = new Map();

export const StyledMenuBox = styled(Box)(({ theme }) => ({
  border: `2px solid ${WsColors.Gray88}`,
  borderRadius: theme.spacing(0.7),
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: WsColors.White,
  width: 40,
  paddingTop: 1,
  paddingBottom: 1,
}));

export const StyledGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: WsColors.White,
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  overflow: 'hidden',
  height: '100%',
}));

const ClientList = ({
  source,
  isChildClient,
  requireHeaderBanner = true,
  isChild = true,
  parentName,
  parentClientId,
}: IClientList) => {
  const [param] = useSearchParams();
  const queryData = param.get('d') ?? '';
  const getDecodedFilters = useCallback((): ClientRequestDTO => {
    if (!queryData) return defaultFilters;
    try {
      return getDecodedQueryParams<ClientRequestDTO>(queryData) ?? defaultFilters;
    } catch (error) {
      console.error('Failed to decode query params:', error);
      return defaultFilters;
    }
  }, [queryData]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
  const [openDialog, closeDialog] = useDialog();
  const [openAssignParentDrawer, setOpenAssignParentDrawer] = useState(false);
  const [openAssignUsersDrawer, setOpenAssignUsersDrawer] = useState(false);
  const [parentList, setParentList] = useState<ClientDTO[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [clientListData, setClientListData] = useState<ClientDTO[]>([]);
  const [searchQuery, setSearchQuery] = useState<ClientRequestDTO>(getDecodedFilters);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [clientStatuses, setClientStatues] = useState<string[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'created_on', sort: 'desc' },
  ]);
  const [settings, setSettings] = useState<FieldOption[]>(defaultColumnSettings);
  const currentUser = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const isAuthorizedAddClient = useClientAuthz(ClientAction.ADD_CLIENT_DETAILS);
  const isAuthorizedViewClientListingAll = useClientAuthz(ClientAction.VIEW_CLIENT_LISTING_ALL);
  const isAuthorizedEditClientDetails = useClientAuthz(ClientAction.EDIT_CLIENT_DETAILS);
  const isAuthorizedAddAssignClient = useClientAuthz(ClientAction.ADD_ASSIGN_CLIENT);
  const [isLoading, setIsLoading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [defaultFilter, setDefaultFilter] = useState<ClientRequestDTO>(defaultFilters);
  const [openClientOwnerFormDialog, setOpenClientOwnerFormDialog] = useState<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<ClientDTO | null>(null);
  const clintName = useClientStore((state) => state.clientName);
  const [clientType, setClientType] = useState('all_clients');
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);
  const [advancedFilterDefaults, setAdvancedFilterDefaults] =
    useState<ClientRequestDTO>(searchQuery);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [openCreateClientDrawer, setOpenCreateClientDrawer] = useState<boolean>(false);
  const [selectedClientForEdit, setSelectedClientForEdit] = useState<ClientDTO | null>(null);

  const fetchParentList = useCallback(async () => {
    try {
      const { data } = await getParentList();
      setParentList(data.filter(({ id }) => !rowSelectionModel.includes(id)));
    } catch (error) {
      setNotification(failed(`Failed to fetch Parent List`));
    }
  }, [rowSelectionModel, setNotification]);

  const getClientDataList = useCallback(async () => {
    setIsLoading(true);
    try {
      const clientStatusListresponse = await getClientStatusList();
      const clientStatusList = new Set();
      statusOptionsData.length = 0;
      clientStatusListresponse.data.forEach((data) => {
        statusOptionsData.push({ label: data.clientStatusName, value: data.id });
        optionsData.set(data.id, data.clientStatusName);
        clientStatusList.add(data.clientStatusName);
      });

      const clientStatusArray = Array.from(clientStatusList);
      clientStatusArray.unshift('All');
      setClientStatues(clientStatusArray as string[]);
      if (isChildClient) {
        const childClientResponse = await getChildClientsByParentId(
          parentClientId ?? '',
          pageNo,
          pageSize
        );
        setClientListData(childClientResponse.data.data);
        setTotalRecord(childClientResponse.data.totalRecord);
      } else {
        const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
        const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;

        const requestDTO: ClientRequestDTO = {
          ...searchQuery,
          pageNo,
          pageSize,
          sortBy: sortBy ?? '',
          sortOrder: sortOrder ?? '',
        };

        const response = await getClientList(requestDTO);

        setDefaultFilter((prev) => ({
          ...prev,
          status: response.data.clientStatuses,
        }));
        setTotalRecord(response.data.totalElements);
        setClientListData(response.data.content);
      }
    } catch (error) {
      console.error(error);
      setNotification(failed(`Failed to fetch Parent List`));
    } finally {
      setIsLoading(false);
    }
  }, [isChildClient, pageNo, pageSize, parentClientId, searchQuery, setNotification, sortModel]);

  useEffect(() => {
    getClientDataList();
  }, [getClientDataList]);

  useEffect(() => {
    const fetchTags = async () => {
      const tagsRes = await getAllClientTags();
      setTags(tagsRes.data);
    };

    fetchTags();
  }, []);

  useEffect(() => {
    setOpenCreateClientDrawer(!!selectedClientForEdit);
  }, [selectedClientForEdit]);

  const onClientOwnerUpdate = async (updatedClient: ClientDTO) => {
    const oldData = clientListData.find((client) => client.id === updatedClient.id);
    if (oldData) {
      oldData.owner = updatedClient.owner;
      const oldOwner = oldData.owner ? oldData.owner.name : ' ';
      const newOwner = updatedClient.owner?.name;
      const logPayload = {
        ...activityLogDTOHelper,
        contextData: {},
        action: LogAction.Edit,
        field: 'Client Owner',
        description: `updated Client Owner from ${oldOwner} to ${newOwner} for`,
        entityId: updatedClient.id,
        entityName: updatedClient.name,
        parentEntitiyType: LogContextType.Client,
        parentEntitiyId: updatedClient.id,
      };
      if (oldData.owner?.email !== updatedClient.owner?.email) {
        await saveActivityLog(logPayload);
      }
      setOpenClientOwnerFormDialog(false);
    }
  };

  useEffect(() => {
    fetchColumnSettings(ColumnSettings.CLIENT_LISTING, ['name', 'action']).then(
      (res) => res && setSettings(res)
    );
  }, []);

  const openClientStatusFormDialog = (defaultValues, clientId, clientName) => {
    clientsAmplitudeEvents('clientlist_status_update', 'Client List Status', currentUser);
    openDialog({
      children: (
        <ClientStatusUpdateForm
          onSave={async () => {
            getClientDataList();
            closeDialog();
          }}
          onCancel={closeDialog}
          options={statusOptionsData}
          clientID={clientId}
          clientName={clientName}
          {...(defaultValues && { defaultValues })}
        />
      ),
    });
  };

  const handleClientEditClick = async (params) => {
    isChild
      ? clientsAmplitudeEvents('clientlist_edit', 'Client List Edit', currentUser)
      : clientsAmplitudeEvents('client_child_edit', 'Client Child Edit', currentUser);

    const response = await getClientById(params.id);
    if (response.status === 200) {
      setSelectedClientForEdit(response.data.data);
    }
  };

  const handleUrlAction = (id, parentName, isChild, tabIndex) => {
    const filters: IFilters = {
      search: searchQuery.query ?? '',
      status: searchQuery.status ?? [],
      location: searchQuery.location ?? initialLocation[0],
      tags: searchQuery.tags ?? [],
    };
    const searchParams = `d=${getEncodedQueryParams(filters)}`;
    const url = `/client-details?clientId=${id}&isChild=${isChild}&tabIndex=${tabIndex}&name=${parentName}&${searchParams}&breadCrumbList=${
      param.get('breadCrumbList')
        ? getEncodedBreadCrumbs(
            getDecodedQueryParams(param.get('breadCrumbList') ?? '{}') as IBreadCrumbsList[],
            clintName
          )
        : getEncodedQueryParams([{ label: 'Clients', url: `/clients?${searchParams}` }])
    }`;
    return url;
  };

  const columnsName: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Client Name',
      minWidth: 200,
      flex: 2,
      renderCell: (params) => (
        <WsLink
          to={handleUrlAction(
            params.row['id'],
            params.row['parentName'],
            !(params.row['parentId'] !== null),
            '0'
          )}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>{params.row['name']}</WsTypography>
        </WsLink>
      ),
    },
    {
      field: 'clientStatusName',
      headerName: 'Client Status',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const clientStatusName = params.row['clientStatusName'];
        return (
          <WsTypography>
            {clientStatusName === true
              ? 'Active'
              : clientStatusName === false
              ? 'Inactive'
              : clientStatusName}
          </WsTypography>
        );
      },
    },
    {
      field: 'owner',
      headerName: 'Client Owner',
      flex: 1,
      renderCell: (params) => (
        <WsLink
          to={`/users/details-page?email=${params.row['owner']?.email}&isLink=true&isUserAnalytics=true`}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            {params.row['owner']?.name}
          </WsTypography>
        </WsLink>
      ),
    },
    {
      field: 'clientContactCount',
      headerName: 'Contacts',
      width: 90,
      sortable: false,
      renderCell: (params) =>
        params.row['parentId'] !== null ? (
          <WsLink
            to={handleUrlAction(params.row['id'], params.row['parentName'], false, '4')}
            onClick={() =>
              clientsAmplitudeEvents('clientlist_contact', 'Client List Contacts', currentUser)
            }
          >
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {params.row['clientContactCount']}
            </WsTypography>
          </WsLink>
        ) : (
          <WsLink
            to={handleUrlAction(
              params.row['id'],
              isChild ? params.row['name'] : parentName,
              isChild,
              '5'
            )}
            onClick={() =>
              clientsAmplitudeEvents('clientlist_contact', 'Client List Contacts', currentUser)
            }
          >
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {params.row['clientContactCount']}
            </WsTypography>
          </WsLink>
        ),
    },
    {
      field: 'country',
      headerName: 'Location',
      flex: 1,
    },
    {
      field: 'industry',
      headerName: 'Industry Type',
      flex: 1,
    },
    {
      field: 'source',
      headerName: 'Source',
      flex: 1,
    },
    {
      field: 'paymentTerms',
      headerName: 'Payment Terms',
      sortable: false,
      flex: 1,
    },
    {
      field: 'website',
      headerName: 'Website',
      flex: 1,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
    },
    {
      field: 'created_by_name',
      headerName: 'Created By',
      flex: 1,
      renderCell: (params) => (
        <WsLink
          to={`/users/details-page?email=${params.row['createdBy']}&isLink=true&isUserAnalytics=true`}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            {params.row['created_by_name']}
          </WsTypography>
        </WsLink>
      ),
    },
    { field: 'created_on', headerName: 'Created On', width: 130 },
    {
      field: 'action',
      headerName: '',
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <PopoverOnClick element={<WsIconMoreVer />}>
          <ListItemButton>
            <WsIconText
              text="Create Note"
              textCursor="pointer"
              icon={<StyledEventNoteIcon />}
              textColor={WsTypoColors.BLACK}
              onClick={() => {
                clientsAmplitudeEvents('clientlist_create_note', 'Client Note Add', currentUser);
                openNotesFormDialog({
                  clientId: params.row['id'],
                  isClient: true,
                  clientName: params.row['name'],
                  openDialog: openDialog,
                  closeDialog: closeDialog,
                });
              }}
            />
          </ListItemButton>
          <ListItemButton>
            <WsIconText
              text="Update Status"
              textCursor="pointer"
              icon={
                <DoneAllSharpIcon sx={{ height: 23, width: 23, color: WsColors.TheBlues500 }} />
              }
              textColor={WsTypoColors.BLACK}
              onClick={() =>
                openClientStatusFormDialog(null, `${params.row['id']}`, `${params.row['name']}`)
              }
            />
          </ListItemButton>
          <ListItemButton>
            <WsIconText
              text="Update Owner"
              textCursor="pointer"
              icon={<PersonIcon sx={{ height: 23, width: 23, color: WsColors.TheBlues500 }} />}
              textColor={WsTypoColors.BLACK}
              onClick={() => {
                setSelectedClient(params.row);
                setOpenClientOwnerFormDialog(true);
              }}
            />
          </ListItemButton>
          {isAuthorizedEditClientDetails && (
            <ListItemButton>
              <WsIconText
                text="Edit"
                textCursor="pointer"
                icon={<EditIcon sx={{ height: 23, width: 23, color: WsColors.TheBlues500 }} />}
                textColor={WsTypoColors.BLACK}
                onClick={() => handleClientEditClick(params)}
              />
            </ListItemButton>
          )}
        </PopoverOnClick>
      ),
    },
  ];

  if (!isChildClient) {
    columnsName.splice(2, 0, {
      field: 'parentName',
      headerName: 'Parent Name',
      renderCell: (params) =>
        isChild && (
          <WsLink to={handleUrlAction(params.row['parentId'], params.row['name'], true, '0')}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {params.row['parentName']}
            </WsTypography>
          </WsLink>
        ),
    });
  }

  if (isChild) {
    columnsName.splice(3, 0, {
      field: 'childCount',
      headerName: 'Child Entity',
      flex: 0.5,
      minWidth: 110,
      sortable: false,
      renderCell: (params) =>
        params.row['parentId'] !== null && isChild == true ? (
          <></>
        ) : (
          <WsLink
            to={handleUrlAction(
              params.row['id'],
              isChild ? params.row['name'] : parentName,
              isChild,
              '2'
            )}
            onClick={() =>
              clientsAmplitudeEvents('clientlist_child', 'Client List Child', currentUser)
            }
          >
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              {params.row['childCount']}
            </WsTypography>
          </WsLink>
        ),
    });
  }

  useEffect(() => {
    const fetchAll = async () => {
      const response = await getClientStatusList();
      response.data.forEach((data) => optionsData.set(data.id, data.clientStatusName));
    };
    fetchAll();
  }, []);

  const tableData = useMemo(
    () =>
      clientListData.map((clientData, index) => ({
        id: clientData.id,
        name: clientData.name,
        childCount: clientData.childCount,
        clientContactCount: clientData.clientContactCount,
        country: !isEmpty(clientData.location)
          ? `${clientData.location?.city ?? ''} ${clientData.location?.state ?? ''} ${
              clientData.location?.country ?? ''
            }`
          : '',
        branch: clientData.branch,
        created_on:
          clientData?.createdOn != undefined
            ? getDateUsingFormat(clientData.createdOn?.toString(), 'DD-MMM-YY')
            : '',
        created_by_name: clientData.createdByName,
        index: index,
        address: clientData.address,
        legal_name: clientData.legalName,
        businessUnit: clientData.businessUnit?.label ?? '',
        industry: clientData.industry ? jobIndustry(clientData?.industry) : '',
        source: clientData.source,
        paymentTerms: clientData.paymentTerms,
        website: clientData.website,
        clientStatusName: clientData.clientStatus?.clientStatusName ?? clientData.isActive,
        parentId: clientData.parentId,
        parentName: clientData.parentName,
        tags: clientData.tags ?? [],
        owner: clientData.owner,
      })),
    [clientListData]
  );

  const handleMerge = async () => {
    try {
      await assignParent(rowSelectionModel as [], selectedParentId as string);
      setNotification(success('Parent assigned successfully !!'));
      await getClientDataList();
      setOpenAssignParentDrawer(false);
    } catch (e) {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
  };

  const onClear = () => {
    setClientType('all_clients');
    setSearchQuery(defaultFilters);
  };

  const navigateToAddClientOptions = () => {
    clientsAmplitudeEvents('client_add', 'Client Creation', currentUser);
    setOpenCreateClientDrawer(true);
  };

  const onFilterFormSubmit = (data: ClientRequestDTO) => {
    const updatedData = {
      ...data,
      status: searchQuery.status || defaultFilter.status,
      clientOwners: searchQuery.clientOwners || defaultFilter.status,
      query: searchQuery.query,
    };
    setSearchQuery((prevQuery) => ({
      ...prevQuery,
      ...mapToClientAdvanceFilter(updatedData),
    }));
    setAdvancedFilterDefaults(data);
  };

  const getClientType = (type: string) => {
    setClientType(type);
    const clientOwners = type === 'my_clients' ? [loggedInUser.email ?? ''] : [];
    setSearchQuery((prevQuery) => ({
      ...prevQuery,
      createdByIds: clientOwners,
    }));
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setPageNo(0);
    setSortModel(sortModel);
  };

  const handleAssignParent = () => {
    setOpenAssignParentDrawer(true);
    fetchParentList();
  };

  const handleSaveParentEntity = async (client: ClientDTO) => {
    await fetchParentList();
    setSelectedParentId(client.id);
    closeDialog();
  };

  const getUpdatedFiltersFromFilters = (filters: ClientRequestDTO) => {
    setSearchQuery(filters);
    setPageNo(0);
  };

  const onClientDrawerClose = () => {
    setOpenCreateClientDrawer(false);
    getClientDataList();
    setSelectedClientForEdit(null);
  };

  return (
    <Grid container rowSpacing={1.5}>
      {requireHeaderBanner && (
        <Grid item xs={12}>
          <CommonHeader
            title={clientType === 'my_clients' ? 'My Clients' : 'All Clients'}
            onSelect={getClientType}
            options={clientTypes}
            isClient={true}
            onCreateClient={() => setOpenCreateClientDrawer(true)}
          />
        </Grid>
      )}
      {clientListData && (
        <Grid container item xs={12} columnSpacing={1}>
          <StyledGrid item xs={openAdvancedFilters ? 9 : 12}>
            {defaultFilter.status && (
              <ClientFilters
                onAdvancedFiltersClick={() => setOpenAdvancedFilters(!openAdvancedFilters)}
                getUpdatedFiltersFromFilters={getUpdatedFiltersFromFilters}
                searchQuery={searchQuery}
                requireHeaderBanner={requireHeaderBanner}
                clientStatuses={clientStatuses}
                tags={tags}
                clear={onClear}
                defaultFilter={defaultFilter}
              />
            )}
            {isAuthorizedViewClientListingAll &&
              settings != null &&
              (clientListData.length > 0 ? (
                <WsDataGrid
                  checkboxSelection
                  autoHeight
                  rows={tableData}
                  columns={columnsName}
                  paginationMode="server"
                  page={pageNo}
                  onPageChange={setPageNo}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  selectionModel={rowSelectionModel}
                  onSelectionModelChange={(newSelectionModel) =>
                    setRowSelectionModel(newSelectionModel)
                  }
                  totalRows={totalRecord}
                  sortingMode="server"
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  columnVisibility
                  columnSettings={settings}
                  onColumnSettingsChange={(data) => {
                    saveColumnSettingsToDB(data, ColumnSettings.CLIENT_LISTING, setNotification);
                    setSettings(data);
                  }}
                />
              ) : (
                <Box
                  sx={{
                    '& .MuiCard-root': {
                      boxShadow: 'none',
                      elevation: 0,
                    },
                  }}
                >
                  {isLoading ? (
                    <WsListingLoader />
                  ) : (
                    (currentUser.owns || isAuthorizedAddClient) && (
                      <NoDataFoundCard
                        text="No results found. Would you like to add a new client?"
                        icon={<EmptyTeamsAndUsersIcon />}
                        subElement={
                          <Box m={2}>
                            <WsButton
                              startIcon={<AddIcon fontSize="small" />}
                              onClick={navigateToAddClientOptions}
                            >
                              Add Client
                            </WsButton>
                          </Box>
                        }
                      />
                    )
                  )}
                </Box>
              ))}
          </StyledGrid>
          {openAdvancedFilters && (
            <Grid item xs={3}>
              <ClientAdvanceFilters
                onClose={() => setOpenAdvancedFilters(false)}
                defaultValues={advancedFilterDefaults}
                onSubmit={onFilterFormSubmit}
                tags={tags}
              />
            </Grid>
          )}
        </Grid>
      )}
      {rowSelectionModel.length > 0 && (
        <Grid item xs={12}>
          <Box mt={16}>
            <BottomBlueBar sx={{ boxShadow: 12, zIndex: 1299 }}>
              <Grid container direction="row" alignItems="center">
                <Grid item xs={1.5}>
                  <WsTypography color={WsTypoColors.WHITE}>
                    {rowSelectionModel.length} entity(s) selected
                  </WsTypography>
                </Grid>
                <Grid item xs={1}>
                  <WsButton
                    variant="outlined"
                    onClick={() => setRowSelectionModel(tableData.map(({ id }) => id as GridRowId))}
                  >
                    Select All
                  </WsButton>
                </Grid>
                <Grid item xs={1.2}>
                  <WsButton variant="outlined" onClick={() => setRowSelectionModel([])}>
                    Unselect All
                  </WsButton>
                </Grid>
                <Grid item xs={5.5}></Grid>
                <Grid item xs={1.4}>
                  {(currentUser.owns || isAuthorizedAddAssignClient) && (
                    <Button variant="contained" onClick={() => setOpenAssignUsersDrawer(true)}>
                      Assign Users
                    </Button>
                  )}
                </Grid>
                <Grid item xs={1.4}>
                  {(currentUser.owns || isAuthorizedAddClient) && source === 'ClientListing' && (
                    <Button variant="contained" onClick={() => handleAssignParent()}>
                      Assign Parent
                    </Button>
                  )}
                </Grid>
              </Grid>
              <WsDrawer
                open={openAssignParentDrawer}
                onClose={() => setOpenAssignParentDrawer(false)}
                title="Select Parent Entity"
              >
                <Grid px={2} py={1} mb={4}>
                  <Button
                    startIcon={<AddIcon />}
                    style={{ margin: '0 0 0.5rem auto' }}
                    onClick={() => {
                      openDialog({
                        children: (
                          <CreateClientForm onClose={closeDialog} onSave={handleSaveParentEntity} />
                        ),
                      });
                    }}
                  >
                    Add Parent Entity
                  </Button>

                  <RadioGroup value={selectedParentId} onChange={(e, v) => setSelectedParentId(v)}>
                    <Grid container alignItems="center">
                      <Grid item xs={6}>
                        <WsTypography bold>Parent Entity</WsTypography>
                      </Grid>
                      <Grid item xs={6}>
                        <WsTypography bold>Location</WsTypography>
                      </Grid>
                      {parentList.map((clientDTO) => (
                        <Grid container key={clientDTO.id}>
                          <Grid item xs={6}>
                            <FormControlLabel
                              sx={{ width: '100%' }}
                              value={clientDTO.id}
                              label={clientDTO.name}
                              control={<Radio />}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            {getClientLocation(clientDTO)}
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>
                </Grid>

                <DrawerFooter>
                  <Grid container direction="row" justifyContent="flex-end" m={2} spacing={2}>
                    <Button
                      size="large"
                      color="error"
                      onClick={() => setOpenAssignParentDrawer(false)}
                    >
                      Cancel
                    </Button>
                    <WsButton size="large" disabled={!selectedParentId} onClick={handleMerge}>
                      Merge
                    </WsButton>
                  </Grid>
                </DrawerFooter>
              </WsDrawer>

              {openAssignUsersDrawer && (
                <WsDrawer
                  open={openAssignUsersDrawer}
                  onClose={() => setOpenAssignUsersDrawer(false)}
                  title="Client Access"
                  headerSize="medium"
                  size="small"
                >
                  <Box p={2} mb={4}>
                    <ClientUserList
                      clientIds={rowSelectionModel as []}
                      onAssign={() => {}}
                      moduleName="CLIENT"
                    />
                  </Box>
                </WsDrawer>
              )}
            </BottomBlueBar>
          </Box>
        </Grid>
      )}

      {openClientOwnerFormDialog && selectedClient && (
        <ClientOwnerPopUp
          id={selectedClient.id}
          entityType="client"
          clientOwner={selectedClient.owner || null}
          open={openClientOwnerFormDialog}
          onCancel={() => {
            setOpenClientOwnerFormDialog(false);
            setSelectedClient(null);
          }}
          onClientSave={onClientOwnerUpdate}
        />
      )}
      {openCreateClientDrawer && (
        <CreateClientDrawer
          open={openCreateClientDrawer}
          onClose={onClientDrawerClose}
          client={selectedClientForEdit}
        />
      )}
    </Grid>
  );
};

export default ClientList;
